import { Bell } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotifications,
  useUnreadNotificationCount,
} from "@/hooks/queries";
import type { NotificationDto } from "@/types/notification.dto";
import {
  formatNotificationRelativeTime,
  getNotificationColor,
  getNotificationIcon,
} from "@/lib/notification-utils";

export type NotificationScope = "admin" | "me";

interface NotificationBellProps {
  scope: NotificationScope;
  viewAllPath: string;
}

const NotificationBell = ({ scope, viewAllPath }: NotificationBellProps) => {
  const navigate = useNavigate();
  const { data: notificationsData } = useNotifications(scope, { pageSize: 20 });
  const { data: unreadCount = 0 } = useUnreadNotificationCount(scope);
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const notifications = notificationsData?.items ?? [];

  const handleNotificationClick = (notification: NotificationDto) => {
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="glass-chip relative h-9 w-9 rounded-xl text-muted-foreground hover:bg-transparent hover:text-foreground"
          aria-label="Notifications"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground ring-2 ring-background dark:ring-black">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={8}
        className="z-50 w-[min(380px,calc(100vw-1rem))] overflow-hidden rounded-xl border border-border bg-popover p-0 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 bg-secondary/40 px-3 py-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="font-heading text-sm font-bold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="h-5 rounded-full px-1.5 text-[10px]">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-[11px] font-semibold text-primary hover:text-primary"
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-[min(400px,calc(var(--radix-popover-content-available-height,100dvh)-6rem))] divide-y divide-border overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</div>
          ) : (
            notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              return (
                <button
                  key={notification.id}
                  type="button"
                  className={cn(
                    "flex w-full items-start gap-3 px-3 py-3 sm:px-4 sm:py-3.5 text-left transition-colors hover:bg-secondary/40",
                    !notification.isRead && "bg-primary/[0.04]",
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      getNotificationColor(notification.type),
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p
                        className={cn(
                          "min-w-0 flex-1 break-words text-sm leading-tight",
                          !notification.isRead
                            ? "font-semibold text-foreground"
                            : "font-medium text-muted-foreground",
                        )}
                      >
                        {notification.title}
                      </p>
                      {!notification.isRead && (
                        <span className="h-2 w-2 shrink-0 animate-pulse-soft rounded-full bg-primary" />
                      )}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{notification.body}</p>
                    <p className="mt-1 text-[10px] font-medium text-muted-foreground">
                      {formatNotificationRelativeTime(notification.createdAt)}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="border-t border-border bg-secondary/30 p-1.5">
          <Button variant="ghost" className="h-8 w-full rounded-lg text-xs font-semibold text-primary hover:text-primary" asChild>
            <Link to={viewAllPath}>View all notifications</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
